<script>
    import { afterUpdate } from "svelte";
    import { onMount } from "svelte";
    import ScreenToggle from "./components/ScreenToggle.svelte";
    import InstallPWAPrompt from "./components/InstallPWAPrompt.svelte";
    import ShareResults from "./components/ShareResults.svelte";
    import TFASelector from "./components/TFASelector.svelte";

    import {
        ulterraTeal,
        super3,
        super2,
        deltaP,
        baseUnit,
        sizeConversionInchesToMM,
        sizeConversionInches32nd,
        flowConversionCubicMeters,
        flowConversionLitersPerMinute,
        flowConversionCubicFeetPerMinute,
        flowConversionBarrelsPerMinute,
        mudwtConversionKilosPerCubicMeter,
        mudwtConversionkPaPerMeter,
        mudwtConversionPSIPer1kFt,
        mudwtConversionPoundsPerCubicFeet,
        mudwtConversionSG,
        pressureConversionKpa,
        pressureConversionBar,
        pressureConversionAtm,
        nozzle_qty_1,
        nozzle_diam_1,
        nozzle_qty_2,
        nozzle_diam_2,
        nozzle_qty_3,
        nozzle_diam_3,
        flow_rate,
        mud_weight,
        bit_size,
        bitSizeDisplayUnit,
        bitSizeConversion,
        bitSizeInInches,
        nozzleDiameterConversion,
        nozzleSizeDisplayUnit,
        tfaConversion,
        tfaDisplayUnit,
        tfaSigFigs,
        flowRateConversion,
        flowRateDisplayUnit,
        flowRateInGpm,
        mudWtConversion,
        mudWtDisplayUnit,
        mudWtInPpg,
        pressureDropConversion,
        pressureDropDisplayUnit,
        pressure_drop_at_bit,
        jetVelocityConversion,
        jetVelocityDisplayUnit,
        jet_velocity,
        impactForceConversion,
        jetImpactForceDisplayUnit,
        jet_impact_force,
        hhpConversion,
        hhpDisplayUnit,
        hhp,
        hsiConversion,
        hsiDisplayUnit,
        hsi,
        tfa_1,
        tfa_2,
        tfa_3,
        tfa_total_in_sq_in,
        tfa_total,
        tfaModalActiveSet,
        tfaModalQty,
        tfaModalSize,
        open,
        range,
    } from "../public/stores"


    let width;
    let offset = 1;
    $: mobile = width < 1080; // note: same as per media query below

    //charts
    const chart_margin_left = 25;
    const chart_margin_top = 20;
    const chart_margin_right = 20;
    const chart_margin_bottom = 25;

    const distanceConversionFtToMeters = 0.3048;
    const areaConversionSqInchesToSqMM = 645.16;

    const powerConversionKw = 0.7457;
    const powerPerAreaConversionKwPerSqureCm = 0.115584;
    const impactForceConversionNewton = 4.4482216152605;

    const nozzle_qty_min = 0;
    const nozzle_qty_max = 20;
    const nozzle_size_min = 8;
    const nozzle_size_max = sizeConversionInches32nd;

    $: chart1Option = "Flow";
    $: chart2Option = "PressureDrop";
    $: unitPreset = "Imperial";

    // ---------------Charting Configuration---------------//
    const you_are_here_dot_size = 8;
    const you_are_here_dot_color = ulterraTeal;
    const line_width = 3;
    const line_color = ulterraTeal;

    //-----------------------------------------------------//
    //------------------Flow vs. HSI CHART-----------------//
    //-----------------------------------------------------//
    $: flowRangeWindow = 150; // 150 (gpm) equivilent
    function flowMinPicker(flowVal) {
        if (flowVal - flowRangeWindow < 0) {
            return 0;
        } else {
            return flowVal - flowRangeWindow;
        }
    }
    $: flowRangeGpm = range(
        flowMinPicker($flowRateInGpm),
        $flowRateInGpm + flowRangeWindow,
        2
    );

    $: flowRangeOutput = flowRangeGpm.map(function (flowValGpm) {
        let converted_flow_val = flowValGpm * $flowRateConversion;
        return converted_flow_val;
    });

    $: powerValsHsi = flowRangeGpm.map(function (flowValGpm) {
        let p_drop =
            ($mudWtInPpg * Math.pow(flowValGpm, 2)) /
            (10858 * Math.pow($tfa_total_in_sq_in, 2));
        let hhp_in_hp = (p_drop * flowValGpm) / 1714;
        let hsi_in_psi =
            hhp_in_hp / (Math.PI * Math.pow($bitSizeInInches / 2, 2));
        return hsi_in_psi;
    });

    $: powerValsOutput = powerValsHsi.map(function (powerValHSI) {
        let converted_power_val = powerValHSI * $hsiConversion;
        return converted_power_val;
    });

    $: hsiVsFlowYAxisMax = 6 * $hsiConversion;

    $: flowVsPowerData = [
        {
            x: flowRangeOutput,
            y: powerValsOutput,
            mode: "lines",
            type: "scatter",
            line: {
                color: line_color,
                width: line_width,
            },
            name: $hsiDisplayUnit,
        },
        {
            x: [$flow_rate],
            y: [$hsi],
            mode: "markers",
            type: "scatter",
            marker: {
                color: you_are_here_dot_color,
                size: you_are_here_dot_size,
            },
            name: "",
        },
    ];

    $: layout = {
        margin: {
            l: 25,
            r: 20,
            b: 20,
            t: 20,
        },
        xaxis: {
            automargin: true,
            title: {
                text: "Flow Rate: (" + $flowRateDisplayUnit + ")",
            },
        },
        yaxis: {
            automargin: true,
            title: {
                text: "Power/Area: " + $hsiDisplayUnit,
            },
            rangemode: "tozero",
            // range: [0, hsiVsFlowYAxisMax],
            hoverformat: ".3r",
        },
        showlegend: false,
    };

    $: config = {
        showSendToCloud: false,
        responsive: true,
        displaylogo: false,
        modeBarButtonsToRemove: [
            "select2d",
            "lasso2d",
            "zoomIn2d",
            "zoomOut2d",
        ],
    };

    //-----------------------------------------------------//
    //------------------HSI vs. TFA CHART------------------//
    //-----------------------------------------------------//
    const maxNozzleDiameterInInches = 24 / 32 / 2;
    $: totalNozzleQty = $nozzle_qty_1 + $nozzle_qty_2 + $nozzle_qty_3;
    $: minTFAInSqIn = $tfa_total_in_sq_in / 2;
    $: maxTFAInSqIn = $tfa_total_in_sq_in + $tfa_total_in_sq_in / 2;
    $: tfaRangeInSqIn = range(minTFAInSqIn, maxTFAInSqIn, 0.025);
    $: tfaRangeOutput = tfaRangeInSqIn.map(function (tfaVal) {
        let converted_tfa_val = tfaVal * $tfaConversion;
        return converted_tfa_val;
    });
    $: powerValsVsTfaHsi = tfaRangeInSqIn.map(function (tfaVal) {
        let p_drop =
            ($mudWtInPpg * Math.pow($flowRateInGpm, 2)) /
            (10858 * Math.pow(tfaVal, 2));
        let hhp_in_hp = (p_drop * $flowRateInGpm) / 1714;
        let hsi_in_psi =
            hhp_in_hp / (Math.PI * Math.pow($bitSizeInInches / 2, 2));
        return hsi_in_psi;
    });
    $: powerValsVsTfaOutput = powerValsVsTfaHsi.map(function (powerValHSI) {
        let converted_power_val = powerValHSI * $hsiConversion;
        return converted_power_val;
    });
    $: powerVsTfaData = [
        {
            x: tfaRangeOutput,
            y: powerValsVsTfaOutput,
            mode: "lines",
            type: "scatter",
            line: {
                color: line_color,
                width: line_width,
            },
            name: $hsiDisplayUnit,
        },
        {
            x: [$tfa_total],
            y: [$hsi],
            mode: "markers",
            type: "scatter",
            marker: {
                color: you_are_here_dot_color,
                size: you_are_here_dot_size,
            },
            name: "",
        },
    ];

    $: powerVsTfaLayout = {
        margin: {
            l: chart_margin_left,
            r: chart_margin_right,
            b: chart_margin_bottom,
            t: chart_margin_top,
        },
        xaxis: {
            automargin: true,
            title: {
                text: "Total Flow Area: (" + $tfaDisplayUnit + super2 + ")",
            },
        },
        yaxis: {
            automargin: true,
            title: {
                text: "Power/Area: " + $hsiDisplayUnit,
            },
            hoverformat: ".3r",
            rangemode: "tozero",
        },
        showlegend: false,
    };

    //==========================================================================
    // -------------  Pressure Drop - Jet Velocity - Impact Force  -------------
    //==========================================================================
    $: pressureDropValsPsi = flowRangeGpm.map(function (flowValGpm) {
        let p_drop =
            ($mudWtInPpg * Math.pow(flowValGpm, 2)) /
            (10858 * Math.pow($tfa_total_in_sq_in, 2));
        return p_drop;
    });

    $: pressureDropValsOutput = pressureDropValsPsi.map(function (
        pressureValPsi
    ) {
        let converted_pressure_val = pressureValPsi * $pressureDropConversion;
        return converted_pressure_val;
    });

    $: flowVsPressureDropData = [
        {
            x: flowRangeOutput,
            y: pressureDropValsOutput,
            mode: "lines",
            type: "scatter",
            line: {
                color: line_color,
                width: line_width,
            },
            name: $pressureDropDisplayUnit,
        },
        {
            x: [$flow_rate],
            y: [$pressure_drop_at_bit],
            mode: "markers",
            type: "scatter",
            marker: {
                color: you_are_here_dot_color,
                size: you_are_here_dot_size,
            },
            name: "",
        },
    ];

    $: flowVsPressureDroplayout = {
        margin: {
            l: chart_margin_left,
            r: chart_margin_right,
            b: chart_margin_bottom,
            t: chart_margin_top,
        },
        xaxis: {
            automargin: true,
            title: {
                text: "Flow Rate: (" + $flowRateDisplayUnit + ")",
            },
        },
        yaxis: {
            automargin: true,
            title: {
                text: "Bit Pressure Drop: " + $pressureDropDisplayUnit,
            },
            rangemode: "tozero",
            hoverformat: ".3r",
        },
        showlegend: false,
    };

    //--Jet Velocity
    $: jetVelocityValsFtPerSec = flowRangeGpm.map(function (flowValGpm) {
        let velocity = 0.321 * (flowValGpm / $tfa_total_in_sq_in);
        return velocity;
    });

    $: jetVelocityValsOutput = jetVelocityValsFtPerSec.map(function (
        velocityValFtS
    ) {
        let converted_velocity_val = velocityValFtS * $jetVelocityConversion;
        return converted_velocity_val;
    });

    $: flowVsJetVelocityData = [
        {
            x: flowRangeOutput,
            y: jetVelocityValsOutput,
            mode: "lines",
            type: "scatter",
            line: {
                color: line_color,
                width: line_width,
            },
            name: $jetVelocityDisplayUnit,
        },
        {
            x: [$flow_rate],
            y: [$jet_velocity],
            mode: "markers",
            type: "scatter",
            marker: {
                color: you_are_here_dot_color,
                size: you_are_here_dot_size,
            },
            name: "",
        },
    ];

    $: flowVsJetVelocitylayout = {
        margin: {
            l: 25,
            r: 20,
            b: 20,
            t: 20,
        },
        xaxis: {
            automargin: true,
            title: {
                text: "Flow Rate: (" + $flowRateDisplayUnit + ")",
            },
        },
        yaxis: {
            automargin: true,
            title: {
                text: "Jet Velocity: " + $jetVelocityDisplayUnit,
            },
            rangemode: "tozero",
            hoverformat: ".3r",
        },
        showlegend: false,
    };

    $: impactForceValsFtLbs = flowRangeGpm.map(function (flowValGpm) {
        let velocity = 0.321 * (flowValGpm / $tfa_total_in_sq_in);
        let impact_force = (flowValGpm * $mudWtInPpg * velocity) / 1930;
        return impact_force;
    });

    $: jetImpactValsOutput = impactForceValsFtLbs.map(function (impactValLbs) {
        let converted_impact_val = impactValLbs * $impactForceConversion;
        return converted_impact_val;
    });

    $: flowVsJetImpactData = [
        {
            x: flowRangeOutput,
            y: jetImpactValsOutput,
            mode: "lines",
            type: "scatter",
            line: {
                color: line_color,
                width: line_width,
            },
            name: $jetImpactForceDisplayUnit,
        },
        {
            x: [$flow_rate],
            y: [$jet_impact_force],
            mode: "markers",
            type: "scatter",
            marker: {
                color: you_are_here_dot_color,
                size: you_are_here_dot_size,
            },
            name: "",
        },
    ];

    $: flowVsJetImpactlayout = {
        margin: {
            l: 25,
            r: 20,
            b: 20,
            t: 20,
        },
        xaxis: {
            automargin: true,
            title: {
                text: "Flow Rate: (" + $flowRateDisplayUnit + ")",
            },
        },
        yaxis: {
            automargin: true,
            title: {
                text: "Jet Impact Force: " + $jetImpactForceDisplayUnit,
            },
            rangemode: "tozero",
            hoverformat: ".3r",
        },
        showlegend: false,
    };

    // ----------------------Chart handling / Callbacks ----------------------------

    // -----------------------------------Cache Version Retrival From Service Worker -----------------------------------
    function requestCacheVersion() {
        //console.log("Requestingbroadcast");
        registerBroadcastReceiver();
        if (navigator.serviceWorker.controller) {
            //console.log("Sendingage to service worker");
            navigator.serviceWorker.controller.postMessage({
                command: "requestCacheVersion",
            });
        } else {
            //console.log("Nove ServiceWorker");
        }
    }
    function registerBroadcastReceiver() {
        navigator.serviceWorker.onmessage = function (event) {
            //console.log("Broadcasted SW : ", event.data);
            var data = event.data;
            if (data.command == "broadcastCacheVersion") {
                update_app_version_display(data.message);
            }
        };
    }
    function update_app_version_display(version) {
        document.getElementById("version-number").textContent = version;
    }

    onMount(() => {
        let plot1Div = document.getElementById("plot1Div");
        let plot2Div = document.getElementById("plot2Div");
        let userInputElements = document.getElementsByClassName("userInput");
        let unitPresetElements = document.getElementsByClassName("unitPreset");
        let Plot = new Plotly.newPlot(
            plot1Div,
            flowVsPowerData,
            layout,
            config
        );
        Plotly.newPlot(
            plot2Div,
            flowVsPressureDropData,
            flowVsPressureDroplayout,
            config
        );

        Array.from(unitPresetElements).forEach(function (element) {
            element.addEventListener("change", updateUnitPresets);
        });
        requestCacheVersion();
    });
    function updateGraphs() {
        let plot1Div = document.getElementById("plot1Div");
        let plot2Div = document.getElementById("plot2Div");
        if (chart1Option === "Flow") {
            Plotly.newPlot(plot1Div, flowVsPowerData, layout, config);
        } else {
            Plotly.newPlot(plot1Div, powerVsTfaData, powerVsTfaLayout, config);
        }

        if (chart2Option === "PressureDrop") {
            Plotly.newPlot(
                plot2Div,
                flowVsPressureDropData,
                flowVsPressureDroplayout,
                config
            );
        } else if (chart2Option === "JetVelocity") {
            Plotly.newPlot(
                plot2Div,
                flowVsJetVelocityData,
                flowVsJetVelocitylayout,
                config
            );
        } else {
            Plotly.newPlot(
                plot2Div,
                flowVsJetImpactData,
                flowVsJetImpactlayout,
                config
            );
        }
    }

    function updateUnitPresets() {
        if (unitPreset === "Imperial") {
            $bitSizeConversion = baseUnit;
            $nozzleDiameterConversion = sizeConversionInches32nd;
            $tfaConversion = baseUnit;
            $flowRateConversion = baseUnit;
            $mudWtConversion = baseUnit;
            $pressureDropConversion = baseUnit;
            $jetVelocityConversion = baseUnit;
            $impactForceConversion = baseUnit;
            $hhpConversion = baseUnit;
            $hsiConversion = baseUnit;
        } else {
            $bitSizeConversion = sizeConversionInchesToMM;
            $nozzleDiameterConversion = sizeConversionInchesToMM;
            $tfaConversion = areaConversionSqInchesToSqMM;
            $flowRateConversion = flowConversionLitersPerMinute;
            $mudWtConversion = mudwtConversionKilosPerCubicMeter;
            $pressureDropConversion = pressureConversionKpa;
            $jetVelocityConversion = distanceConversionFtToMeters;
            $impactForceConversion = impactForceConversionNewton;
            $hhpConversion = powerConversionKw;
            $hsiConversion = powerPerAreaConversionKwPerSqureCm;
        }
    }

    afterUpdate(() => {
        // ...the DOM is now in sync with the data
        updateGraphs();
    });
    //--------------------------Screen toggle options-------------------------//
    let screen_toggle_options = [
        {
            label: "Units",
            class: "feather feather-check-circle",
            path_d: "M22 11.08V12a10 10 0 1 1-5.93-9.14",
            path_d2: "",
            polyline: "22 4 12 14.01 9 11.01",
        },
        {
            label: "Inputs",
            class: "feather feather-edit",
            path_d:
                "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7",
            path_d2: "M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z",
            polyline: "",
        },
        {
            label: "Graphs",
            class: "feather feather-pie-chart",
            path_d: "M21.21 15.89A10 10 0 1 1 8 2.83",
            path_d2: "M22 12A10 10 0 0 0 12 2v10z",
            polyline: "",
        },
    ];

    //TFA Selectors Clear
    function openTFAModal(nozzle_set) {
        if (nozzle_set === 1) {
            $tfaModalActiveSet = 1;
            $tfaModalQty = $nozzle_qty_1;
            $tfaModalSize = $nozzle_diam_1;
        } else if (nozzle_set === 2) {
            $tfaModalActiveSet = 2;
            $tfaModalQty = $nozzle_qty_2;
            $tfaModalSize = $nozzle_diam_2;
        } else {
            $tfaModalActiveSet = 3;
            $tfaModalQty = $nozzle_qty_3;
            $tfaModalSize = $nozzle_diam_3;
        }
        return $open = !$open;;
    }

    function clear_nozzle_set(nozzle_set) {
        if (nozzle_set === 1) {
            nozzle_qty_1.set(0);
            nozzle_diam_1.set(0);
        } else if (nozzle_set === 2) {
            nozzle_qty_2.set(0);
            nozzle_diam_2.set(0);
        } else {
            nozzle_qty_3.set(0);
            nozzle_diam_3.set(0);
        }
    }
</script>

<!--====================================================================================================================
	                                                   STYLE
=====================================================================================================================-->
<style>
    :root {
        --input-size: 1.15rem;
        --ulterra-teal: rgb(0, 146, 159);
        --ulterra-logo-grey: rgb(76, 78, 85);
        --uconnect-grey: #d3d3d3;
        --inovative-green: #98c21f;
    }

    input[type="radio"] {
        width: var(--input-size);
        height: var(--input-size);
        vertical-align: middle;
    }

    label {
        font-size: var(--input-size);
        vertical-align: middle;
        margin: 0;
    }

    .nozzle-group-input {
        margin-bottom: 5px;
        margin-top: 5px;
    }
    .userInput,
    .unitPreset {
        text-align: center;
        font-size: var(--input-size);
        height: 30px;
    }
    .units-card-item {
        font-size: var(--input-size);
        margin: 0;
        justify-self: start;
        padding-right: 10px;
    }

    .calculated-output-value {
        font-size: var(--input-size);
        color: black;
        font-weight: bold;
        margin-top: -10px;
    }

    .card {
        margin-top: 10px;
        border: none;
    }

    .card-header {
        background-color: rgb(0, 146, 159);
        color: white;
        padding-top: 5px;
        padding-bottom: 5px;
        height: 35px;
    }

    .card-body {
        padding-top: 10px;
        padding-bottom: 10px;
    }
    .card-divider {
        margin-top: 7px;
        margin-bottom: 7px;
    }

    .units-card {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        align-items: center;
        row-gap: 2px;
    }

    .add-nozzles:hover {
        fill: rgba(0, 146, 159, 0.25);
        cursor: pointer;
    }
    .horizontal-radio-label {
        justify-self: end;
    }
    .last-element-on-card {
        margin-bottom: 0;
    }

    #bit-size-and-tfa,
    #fluid-inputs {
        display: grid;
        grid-template-columns: 1fr, 1fr;
        justify-items: center;
        align-items: center;
    }

    #nozzle-groups {
        display: grid;
        grid-template-columns: 1fr, 1fr, 1fr, 1fr;
        justify-items: center;
        align-items: center;
    }

    #outputs-grid {
        display: grid;
        justify-items: center;
        align-items: center;
        grid-template-columns: 50% 50%;
        column-gap: 5px;
        row-gap: 10px;
    }

    .quick-calc-chart,
    .plot-container {
        height: 100%;
        width: 100%;
    }

    .offset-1 {
        transform: translate(-33.333%, 0);
    }
    .offset-2 {
        transform: translate(-66.666%, 0);
    }

    .viewport {
        display: grid;
        width: 300%;
        height: 100%;
        grid-template-columns: 1fr 1fr 1fr;
        justify-items: center;
        grid-auto-rows: 100%;
        transition: transform 0.3s;
        margin: 0;
    }

    .page-content-container {
        position: relative;
        height: 100vh;
        overflow: hidden;
        padding-top: 66px;
        box-sizing: border-box;
        padding-bottom: 76px; /*  1rem/10px more than screen toggle height */
        background-color: var(--uconnect-grey);
    }
    #basic-outputs-card {
        height: 240px;
    }

    @media (min-width: 1080px) {
        .page-content-container {
            padding-bottom: 1rem;
        }

        .viewport {
            width: 100%;
            height: 100%;
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            grid-auto-rows: 100%;
            justify-items: center;
            transition: none;
            max-width: 1400px;
            margin-left: auto;
            margin-right: auto;
        }

        .offset-1,
        .offset-2 {
            transform: none;
        }
    }


</style>

<!--=====================a===============================================================================================
	                                                   HTML
=====================================================================================================================-->

<!-- Image and text class="navbar-brand" -->
<nav
    class="navbar navbar-light fixed-top"
    style="border-bottom: 1px solid darkgrey; font-family: Rift; background-color: var(--main-background-color); color: rgb(76, 78, 85);">
    <a href="https://ulterra.com/">
        <img
            src="/Ulterra Horizontal Dark Gray.png"
            height="50px"
            alt="Ulterra Logo"
            loading="lazy" />
    </a>

    <div style="display: block; text-align: center">
        <div>Bit Hydraulics</div>
        <div id="version-number" style="font-size: 8pt; margin-top: -5px;">
            Application Version:
        </div>
    </div>
</nav>

<div class="page-content-container" bind:clientWidth={width}>
    <div class="viewport offset-{offset}">
        <!--Units Column-->
        <div id="units-column" class="col-sm" style="max-width: 400px">
            <!--Bit Size, Nozzle Size, TFA Card-->
            <div class="card">
                <div class="card-header">
                    <label style="font-size: 1em;">
                        Unit Presets:
                        <input
                            type="radio"
                            style="width: 1em; height: 1em"
                            bind:group={unitPreset}
                            value={'Imperial'}
                            class="unitPreset" />
                        Imperial
                        <input
                            type="radio"
                            style="width: 1em; height: 1em"
                            bind:group={unitPreset}
                            value={'Metric'}
                            class="unitPreset" />
                        Metric
                    </label>
                </div>
                <div
                    class="card-body units-card"
                    style="grid-template-columns: 40% 25% 35%">
                    <p
                        style="grid-column-start: 1;"
                        class="units-card-item horizontal-radio-label">
                        Bit Size:
                    </p>
                    <label style="grid-column-start: 2;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$bitSizeConversion}
                            value={baseUnit} />
                        in
                    </label>
                    <label style="grid-column-start: 3;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$bitSizeConversion}
                            value={sizeConversionInchesToMM} />
                        mm
                    </label>
                    <p
                        style="grid-column-start: 1;"
                        class="units-card-item horizontal-radio-label">
                        Nozzles:
                    </p>
                    <label style="grid-column-start: 2;">
                        <input
                            type="radio"
                            class="userInput"
                            bind:group={$nozzleDiameterConversion}
                            value={sizeConversionInches32nd} />
                        in<sub>32nd</sub>
                    </label>
                    <label style="grid-column-start: 3;">
                        <input
                            type="radio"
                            class="userInput"
                            bind:group={$nozzleDiameterConversion}
                            value={sizeConversionInchesToMM} />
                        mm
                    </label>
                    <p
                        style="grid-column-start: 1;"
                        class="units-card-item horizontal-radio-label">
                        TFA:
                    </p>
                    <label style="grid-column-start: 2;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$tfaConversion}
                            value={baseUnit} />
                        in<sup>2</sup>
                    </label>
                    <label style="grid-column-start: 3;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$tfaConversion}
                            value={areaConversionSqInchesToSqMM} />
                        mm<sup>2</sup>
                    </label>
                </div>
            </div>
            <!--Fluid Properties Card-->
            <div class="card">
                <div class="card-header">Fluid Property Units</div>
                <div class="card-body units-card">
                    <p
                        style="grid-column-start: 1; grid-row-start: 1;"
                        class="units-card-item">
                        Flow Rate:
                    </p>
                    <label style="grid-column-start: 1;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$flowRateConversion}
                            value={baseUnit} />
                        gpm
                    </label>
                    <label style="grid-column-start: 1;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$flowRateConversion}
                            value={flowConversionCubicMeters} />
                        m<sup>3</sup>/min
                    </label>
                    <label style="grid-column-start: 1;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$flowRateConversion}
                            value={flowConversionLitersPerMinute} />
                        l/min
                    </label>
                    <label style="grid-column-start: 1;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$flowRateConversion}
                            value={flowConversionCubicFeetPerMinute} />
                        ft<sup>3</sup>/min
                    </label>
                    <label style="grid-column-start: 1;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$flowRateConversion}
                            value={flowConversionBarrelsPerMinute} />
                        bbl/min
                    </label>
                    <p
                        style="grid-column-start: 2; grid-row-start: 1;"
                        class="units-card-item">
                        Mud Wt:
                    </p>
                    <label style="grid-column-start: 2; grid-row-start: 2;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$mudWtConversion}
                            value={baseUnit} />
                        ppg
                    </label>
                    <label style="grid-column-start: 2; grid-row-start: 3;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$mudWtConversion}
                            value={mudwtConversionKilosPerCubicMeter} />
                        kg/m<sup>3</sup>
                    </label>
                    <label style="grid-column-start: 2; grid-row-start: 4;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$mudWtConversion}
                            value={mudwtConversionkPaPerMeter} />
                        kPa/m
                    </label>
                    <label style="grid-column-start: 2; grid-row-start: 5;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$mudWtConversion}
                            value={mudwtConversionPSIPer1kFt} />
                        psi/1kft
                    </label>
                    <label style="grid-column-start: 2; grid-row-start: 6;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$mudWtConversion}
                            value={mudwtConversionPoundsPerCubicFeet} />
                        lb/ft<sup>3</sup>
                    </label>
                    <label style="grid-column-start: 2;  grid-row-start: 7;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$mudWtConversion}
                            value={mudwtConversionSG} />
                        sg
                    </label>

                    <p
                        style="grid-column-start: 3; grid-row-start: 1;"
                        class="units-card-item">
                        Pressure:
                    </p>
                    <label style="grid-column-start: 3; grid-row-start: 2;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$pressureDropConversion}
                            value={baseUnit} />
                        psi
                    </label>
                    <label style="grid-column-start: 3; grid-row-start: 3;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$pressureDropConversion}
                            value={pressureConversionKpa} />
                        kPa
                    </label>
                    <label style="grid-column-start: 3; grid-row-start: 4;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$pressureDropConversion}
                            value={pressureConversionBar} />
                        bar
                    </label>
                    <label style="grid-column-start: 3; grid-row-start: 5;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$pressureDropConversion}
                            value={pressureConversionAtm} />
                        atm
                    </label>
                </div>
            </div>
            <!--Hydraulic Power Card-->
            <div class="card">
                <div class="card-header">Hydraulic Power Units</div>
                <div
                    class="card-body units-card"
                    style="grid-template-columns: 40% 25% 35%">
                    <p
                        style="grid-column-start: 1;"
                        class="units-card-item horizontal-radio-label">
                        Jet Velocity:
                    </p>
                    <label style="grid-column-start: 2;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$jetVelocityConversion}
                            value={baseUnit} />
                        ft/s
                    </label>
                    <label style="grid-column-start: 3;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$jetVelocityConversion}
                            value={distanceConversionFtToMeters} />
                        m/s
                    </label>
                    <p
                        style="grid-column-start: 1;"
                        class="units-card-item horizontal-radio-label">
                        Jet Impact:
                    </p>
                    <label style="grid-column-start: 2;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$impactForceConversion}
                            value={baseUnit} />
                        lbf
                    </label>
                    <label style="grid-column-start: 3;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$impactForceConversion}
                            value={impactForceConversionNewton} />
                        N
                    </label>
                    <p
                        style="grid-column-start: 1;"
                        class="units-card-item horizontal-radio-label">
                        Power:
                    </p>
                    <label style="grid-column-start: 2;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$hhpConversion}
                            value={baseUnit} />
                        Hp
                    </label>
                    <label style="grid-column-start: 3;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$hhpConversion}
                            value={powerConversionKw} />
                        kW
                    </label>
                    <p
                        style="grid-column-start: 1;"
                        class="units-card-item horizontal-radio-label">
                        Power/Area:
                    </p>
                    <label style="grid-column-start: 2;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$hsiConversion}
                            value={baseUnit} />
                        hsi
                    </label>
                    <label style="grid-column-start: 3;">
                        <input
                            class="userInput"
                            type="radio"
                            bind:group={$hsiConversion}
                            value={powerPerAreaConversionKwPerSqureCm} />
                        kW/cm<sup>2</sup>
                    </label>
                </div>
            </div>
        </div>
        <!--Inputs Column-->
        <div id="inputs-column" class="col-sm" style="max-width: 400px">
            <!--Bit Inputs Card-->
            <div class="card">
                <div class="card-header">Bit & Drilling Fluid Inputs</div>
                <div class="card-body">
                    <div id="bit-size-and-tfa">
                        <div>Bit Size:</div>
                        <label style="grid-row-start: 2; grid-row-end: span 1">
                            <input
                                class="userInput"
                                type="number"
                                inputmode="decimal"
                                onClick="this.select()"
                                bind:value={$bit_size}
                                min="2.5"
                                max="600" />
                            {$bitSizeDisplayUnit}
                        </label>
                        <div style="grid-column-start: 2">Total TFA:</div>
                        <div
                            class="calculated-output-value"
                            style="grid-column-start: 2; grid-row-start: 2; grid-row-end: span 1; margin: 0;">
                            {$tfa_total.toFixed($tfaSigFigs)}
                            ({$tfaDisplayUnit}<sup>2</sup>)
                        </div>
                    </div>
                    <hr class="card-divider" />

                    <!--NOZZLE GROUPS-->
                    <div id="nozzle-groups">
                        <div style="grid-column-start: 1;">Nozzles:</div>
                        <div style="grid-column-start: 2;">Qty:</div>
                        <div style="grid-column-start: 3;">
                            Size
                            {$nozzleSizeDisplayUnit}:
                        </div>
                        <div style="grid-column-start: 4;">
                            TFA ({$tfaDisplayUnit}<sup>2</sup>):
                        </div>

                        <div style="grid-column-start: 1; grid-row-start: 2;">
                            <svg
                                on:click={() => clear_nozzle_set(1)}
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="feather feather-minus-circle add-nozzles"><circle
                                    cx="12"
                                    cy="12"
                                    r="10" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                            <svg
                                on:click={() => openTFAModal(1)}
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="feather feather-plus-circle add-nozzles"><circle
                                    cx="12"
                                    cy="12"
                                    r="10" />
                                <line x1="12" y1="8" x2="12" y2="16" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                        </div>
                        <input
                            style="grid-column-start: 2; grid-row-start: 2;"
                            type="number"
                            inputmode="decimal"
                            onClick="this.select()"
                            class="userInput nozzle-group-input"
                            bind:value={$nozzle_qty_1}
                            min={nozzle_qty_min}
                            max={nozzle_qty_max} />
                        <input
                            style="grid-column-start: 3; grid-row-start: 2;"
                            type="number"
                            inputmode="decimal"
                            class="userInput nozzle-group-input"
                            onClick="this.select()"
                            bind:value={$nozzle_diam_1}
                            min={nozzle_size_min}
                            max={nozzle_size_max} />
                        <div style="grid-column-start: 4; grid-row-start: 2;">
                            {$tfa_1.toFixed($tfaSigFigs)}
                        </div>

                        <div style="grid-column-start: 1; grid-row-start: 3;">
                            <svg
                                on:click={() => clear_nozzle_set(2)}
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="feather feather-minus-circle add-nozzles"><circle
                                    cx="12"
                                    cy="12"
                                    r="10" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                            <svg
                                on:click={() => openTFAModal(2)}
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="feather feather-plus-circle add-nozzles"><circle
                                    cx="12"
                                    cy="12"
                                    r="10" />
                                <line x1="12" y1="8" x2="12" y2="16" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                        </div>
                        <input
                            style="grid-column-start: 2; grid-row-start: 3;"
                            type="number"
                            inputmode="decimal"
                            class="userInput nozzle-group-input"
                            onClick="this.select()"
                            bind:value={$nozzle_qty_2}
                            min={nozzle_qty_min}
                            max={nozzle_qty_max} />
                        <input
                            style="grid-column-start: 3; grid-row-start: 3;"
                            type="number"
                            inputmode="decimal"
                            class="userInput nozzle-group-input"
                            onClick="this.select()"
                            bind:value={$nozzle_diam_2}
                            min={nozzle_size_min}
                            max={nozzle_size_max} />
                        <div style="grid-column-start: 4; grid-row-start: 3;">
                            {$tfa_2.toFixed($tfaSigFigs)}
                        </div>

                        <div style="grid-column-start: 1; grid-row-start: 4;">
                            <svg
                                on:click={() => clear_nozzle_set(3)}
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="feather feather-minus-circle add-nozzles"><circle
                                    cx="12"
                                    cy="12"
                                    r="10" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                            <svg
                                on:click={() => openTFAModal(3)}
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="feather feather-plus-circle add-nozzles"><circle
                                    cx="12"
                                    cy="12"
                                    r="10" />
                                <line x1="12" y1="8" x2="12" y2="16" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                        </div>
                        <input
                            style="grid-column-start: 2; grid-row-start: 4;"
                            type="number"
                            inputmode="decimal"
                            class="userInput nozzle-group-input"
                            onClick="this.select()"
                            bind:value={$nozzle_qty_3}
                            min={nozzle_qty_min}
                            max={nozzle_qty_max} />
                        <input
                            style="grid-column-start: 3; grid-row-start: 4;"
                            type="number"
                            inputmode="decimal"
                            class="userInput nozzle-group-input"
                            onClick="this.select()"
                            bind:value={$nozzle_diam_3}
                            min={nozzle_size_min}
                            max={nozzle_size_max} />
                        <div style="grid-column-start: 4; grid-row-start: 4;">
                            {$tfa_3.toFixed($tfaSigFigs)}
                        </div>
                    </div>
                    <hr class="card-divider" />
                    <div id="fluid-inputs">
                        <div>Flow Rate:</div>
                        <label style="grid-row-start: 2; grid-column-start: 1;">
                            <input
                                style="width: 70px;"
                                id="flowInputNumber"
                                class="userInput"
                                onclick="this.select()"
                                type="number"
                                inputmode="decimal"
                                bind:value={$flow_rate}
                                min="0"
                                max="2000" />
                            ({$flowRateDisplayUnit})
                        </label>

                        <div style="grid-row-start: 1; grid-column-start: 2;">
                            Mud Weight:
                        </div>
                        <label style="grid-row-start: 2; grid-column-start: 2;">
                            <input
                                style="display: inline-block"
                                class="userInput"
                                onclick="this.select()"
                                type="number"
                                inputmode="decimal"
                                bind:value={$mud_weight}
                                min="8.4"
                                max="25" />
                            ({$mudWtDisplayUnit})
                        </label>
                    </div>
                </div>
            </div>
            <!--Basic Outputs Card-->
            <div class="card" id="basic-outputs-card">
                <div
                    class="card-header"
                    style="display: flex; justify-content: space-between; align-items: center;">
                    <div>Basic Outputs</div>
                    <ShareResults />
                </div>
                <div class="card-body" id="outputs-grid" style="flex: none;">
                    <div>Jet Velocity:</div>
                    <div>Jet Impact Force:</div>
                    <div class="calculated-output-value">
                        <b>{$jet_velocity.toFixed(1)}
                            {$jetVelocityDisplayUnit}</b>
                    </div>
                    <div class="calculated-output-value">
                        <b>{$jet_impact_force.toFixed(1)}
                            {$jetImpactForceDisplayUnit}</b>
                    </div>
                    <div>Bit Pressure Drop:</div>
                    <div>Hydraulic Power:</div>
                    <div class="calculated-output-value">
                        <b>{Math.round($pressure_drop_at_bit)}
                            {$pressureDropDisplayUnit}</b>
                    </div>
                    <div class="calculated-output-value">
                        <b>{$hhp.toFixed(1)} {$hhpDisplayUnit}</b>
                    </div>
                    <div style="grid-column: 1 / span 2;">
                        Hydraulic Power/Area:
                    </div>
                    <div
                        class="calculated-output-value"
                        style="grid-column: 1 / span 2;">
                        <b>{$hsi.toFixed(2)} {$hsiDisplayUnit}</b>
                    </div>
                </div>
            </div>
        </div>

        <!--Graph Outputs Column-->
        <div id="graphs-column" class="col-sm" style="max-width: 600px">
            <div class="card">
                <div class="card-header">
                    <label style="font-size: 1em;">
                        HSI vs.
                        <input
                            type="radio"
                            style="width: 1em; height: 1em"
                            bind:group={chart1Option}
                            value={'Flow'}
                            class="userInput" />
                        Flow
                        <input
                            type="radio"
                            style="width: 1em; height: 1em"
                            bind:group={chart1Option}
                            value={'TFA'}
                            class="userInput" />
                        TFA
                    </label>
                </div>
                <div class="card-body" style="height: 253.5px;">
                    <div id="plot1Div" class="quick-calc-chart">
                        <!-- Plotly chart will be drawn inside this DIV -->
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-header">
                    <label style="font-size: 1em;">
                        <input
                            type="radio"
                            style="width: 1em; height: 1em"
                            bind:group={chart2Option}
                            value={'PressureDrop'}
                            class="userInput" />
                        Bit
                        {deltaP}P
                        <input
                            type="radio"
                            style="width: 1em; height: 1em"
                            bind:group={chart2Option}
                            value={'JetVelocity'}
                            class="userInput" />
                        Jet V
                        <input
                            type="radio"
                            style="width: 1em; height: 1em"
                            bind:group={chart2Option}
                            value={'JetImpact'}
                            class="userInput" />
                        Jet IF vs. Flow
                    </label>
                </div>
                <div class="card-body" style="height: 253.5px;">
                    <div id="plot2Div" class="quick-calc-chart">
                        <!-- Plotly chart will be drawn inside this DIV -->
                    </div>
                </div>
            </div>
        </div>
    </div>
    <TFASelector />
    {#if mobile}
        <ScreenToggle bind:offset options={screen_toggle_options} />
        <InstallPWAPrompt />
    {/if}
</div>
